<template>
    <b-spinner v-if="!model"></b-spinner>
    <div v-else>
        <h4>{{ model.name }}
            <b-button variant="success" @click="save()" class="ml-3">{{ $t('save') }}</b-button>
            <b-button variant="warning" @click="testModal = true" class="ml-3">{{ $t('test') }}</b-button>
            <protokol-settings-dropdown size="sm" class="pull-right" v-if="isPlatformIntegrator">
                <b-dropdown-item-button @click="remove" > {{ $t('delete') }} </b-dropdown-item-button>
            </protokol-settings-dropdown>
        </h4>
        <b-tabs class="mt-5">
            <b-tab :title="$t('general')">
                <b-row>
                    <b-col cols="12" md="6">
                        <b-row>
                            <b-col cols="12" md="4" class="mb-2">
                                <b-input-group-append>
                                    <b-dropdown :text="`IDE: ${ideVersion}`">
                                        <b-dropdown-item v-for="version in availableVersion" :key="version"
                                            @click="previewVersion = version"> {{ version }} </b-dropdown-item>
                                        <b-dropdown-divider />
                                        <b-dropdown-item @click="forkModal = true" v-if="model.dev_version != ''"> {{
                                            $t('fork') }} </b-dropdown-item>
                                        <b-dropdown-item @click="removeVersion()" v-if="model.dev_version != '' && canRemoveVersion"> {{
                                            $t('remove') }} </b-dropdown-item>
                                    </b-dropdown>
                                </b-input-group-append>
                            </b-col>
                            <b-col cols="4" md="4" class="mb-2">
                                <b-dropdown :text="'Dev: ' + devVersion">
                                    <b-dropdown-item v-for="version in availableVersion" :key="version"
                                        @click="publishDevVersion(version)">{{ version }} </b-dropdown-item>
                                    <b-dropdown-divider />
                                    <b-dropdown-item @click="publishDevVersion('')" v-if="model.dev_version != ''"> {{
                                        $t('deactivate') }} </b-dropdown-item>
                                </b-dropdown>
                            </b-col>
                            <b-col cols="12" md="4">
                                <b-dropdown :text="'Live: ' + liveVersion">
                                    <b-dropdown-item v-for="version in availableVersion" :key="version"
                                        @click="publishLiveVersion(version)">{{ version }} </b-dropdown-item>
                                    <b-dropdown-divider />
                                    <b-dropdown-item @click="publishLiveVersion('')" v-if="model.public_version != ''">
                                        {{
                                            $t('unpublish') }} </b-dropdown-item>
                                </b-dropdown>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col cols="12">
                        <CodeEditor :withButton="false" v-model="expression"
                            :readOnly="previewVersion == model.public_version" />
                    </b-col>
                    <b-col cols="12" v-if="testOutput">
                        <p> Test output </p>
                        <CodeEditor lang="application/json" :controls="false"
                            :value="JSON.stringify(testOutput, null, 2)" :withButton="false"></CodeEditor>
                    </b-col>
                </b-row>
            </b-tab>
            <b-tab :title="$t('environment')">
                <b-form-group :label="$t('environment')" :label-cols="2">
                    <b-dropdown :text="env">
                        <b-dropdown-item @click="env = 'dev'"> dev </b-dropdown-item>
                        <b-dropdown-item @click="env = 'live'"> live </b-dropdown-item>
                    </b-dropdown>
                </b-form-group>
                <b-form-group :label="$t('envVariables')" :label-cols="12">
                    <CodeEditor lang="application/json" :controls="false" v-model="envVariables" :withButton="false">
                    </CodeEditor>
                </b-form-group>
            </b-tab>
            <b-tab :title="$t('settings')">
                <b-form-group :label="$t('tags')" :description="this.$t('tagsFunctionSettingTab')" label-for="tag" :label-cols="2" :horizontal="true">
                    <v-select v-model.trim="model.tags" taggable multiple @input="formatTags" />
                </b-form-group>
                <b-form-group :description="this.$t('publicFunctionSettingTab')" :label="$t('public')"
                    label-for="public" :label-cols="2" :horizontal="true">
                    <c-switch color="success" variant="pill" v-model="isPublic" :disabled="!isPlatformIntegrator"/>
                </b-form-group>
                <b-form-group :label="$t('apiUser')"
                    :description="this.$t('apiUserFunctionSettingTab')"
                    v-if="model.public"
                    label-for="public" :label-cols="2" :horizontal="true">
                    <v-select :options="apiUsers" v-model="model.config.api_user" :loading="!apiUsers" label="username" :reduce="o => o.uuid"/>
                </b-form-group>
                <b-form-group :label="$t('endpoint')"
                    v-if="model.public"
                    label-for="public" :label-cols="2" :horizontal="true">
                    <b-input-group>
                        <b-input-prepend>
                            <b-dropdown :text="publicEndpointEnv">
                                <b-dropdown-item @click="updateEndpointEnv('dev')">dev</b-dropdown-item>
                                <b-dropdown-item @click="updateEndpointEnv('live')">live</b-dropdown-item>
                            </b-dropdown>
                        </b-input-prepend>
                        <b-form-input disabled  v-if="publicEndpoint" v-model="publicEndpoint"/>
                    </b-input-group>
                </b-form-group>
            </b-tab>
        </b-tabs>

        <b-modal v-model="forkModal" :ok-title="$t('fork')" @ok="forkVersion">
            <b-form-group :label="$t('version')">
                <b-input v-model="previewVersion" disabled></b-input>
            </b-form-group>
            <b-form-group :label="$t('newVersion')">
                <b-input v-model="forkTo"></b-input>
            </b-form-group>
        </b-modal>

        <b-sidebar right title="Test function" v-model="testModal">
            <div class="p-2">
                <b-tabs>
                    <b-tab title="Body">
                        <CodeEditor lang="application/json" :controls="false" v-model="testInputDataBody"
                            :withButton="false"></CodeEditor>
                    </b-tab>
                    <b-tab title="Query">
                        <CodeEditor lang="application/json" :controls="false" v-model="testInputDataQuery"
                            :withButton="false"></CodeEditor>
                    </b-tab>
                    <b-tab title="Headers">
                        <CodeEditor lang="application/json" :controls="false" v-model="testInputDataHeaders"
                            :withButton="false"></CodeEditor>
                    </b-tab>
                </b-tabs>
                <b-button variant="success" @click="runTest" class="mt-2">{{ $t('test') }}</b-button>
            </div>
        </b-sidebar>
    </div>
</template>

<script>
import CodeEditor from '@/components/form/Editor/Code'
import { get, set, cloneDeep } from 'lodash'
import VSelect from 'vue-select'
import { Switch as cSwitch } from '@coreui/vue'

export default {
    name: 'FunctionEdit',
    components: {
        CodeEditor,
        VSelect,
        cSwitch
    },
    data() {
        return {
            model: null,
            ideVersion: this.$t('choose'),
            forkModal: false,
            testModal: false,
            forkTo: null,
            env: 'dev',
            testInput: {
                body: {},
                query: {},
                headers: {}
            },
            testOutput: null,
            apiUsers: null,
            publicEndpoint: null,
            publicEndpointEnv: 'dev'
        }
    },
    computed: {
        availableVersion() {
            return this.model.settings.map(s => s.version)
        },
        previewVersion: {
            get() {
                return this.model.dev_version
            },
            set(val) {
                this.ideVersion = val
            }
        },
        liveVersion() {
            return this.model.public_version || 'not live'
        },
        devVersion() {
            return this.model.dev_version || 'not active'
        },
        envVariables: {
            get() {
                return JSON.stringify(get(this.model.env_variables, this.env, {}) || {}, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                set(this.model, `env_variables.${this.env}`, vars)
            }
        },
        testInputDataBody: {
            get() {
                return JSON.stringify(this.testInput.body, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                this.testInput.body = vars
            }
        },
        testInputDataQuery: {
            get() {
                return JSON.stringify(this.testInput.query, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                this.testInput.query = vars
            }
        },
        testInputDataHeaders: {
            get() {
                return JSON.stringify(this.testInput.headers, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                this.testInput.headers = vars
            }
        },
        expression: {
            get() {
                let settings = this.model.settings.find(s => s.version == this.ideVersion)
                return get(settings, 'expr', null)
            },
            set(val) {
                let settings = this.model.settings.find(s => s.version == this.ideVersion)
                settings.expr = val
            }
        },

        isPublic: {
            get() {
                return this.model.public
            },

            set(val) {
                if (val) {
                    this.$http.get(`/v1/system/function/signature/${this.publicEndpointEnv}/${this.model.uuid}`).then(resp => {
                        const { data } = resp
                        
                        this.publicEndpoint = data.signature
                    })
                }

                this.model.public = val
            }
        },

        isPlatformIntegrator() {
            return this.model.integrator == "platform"
        },

        canRemoveVersion() {
            return this.model.settings.length > 1
        }
    },
    methods: {
        removeVersion() {
            this.model.settings = this.model.settings.filter(s => s.version != this.ideVersion)
            this.save()
            this.previewVersion = this.devVersion
        },
        async publishLiveVersion(version) {
            let confirm = await this.$bvModal.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            this.model.public_version = version
            this.save()
        },
        async publishDevVersion(version) {
            let confirm = await this.$bvModal.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            this.model.dev_version = version
            this.save()
        },
        forkVersion() {
            let settings = this.model.settings.find(s => s.version == this.previewVersion)
            let settingsToFork = cloneDeep(settings)
            settingsToFork.version = this.forkTo
            this.model.settings.push(settingsToFork)
            this.save()
        },
        save() {
            if (this.model) {
                let popup = this.$loadingPopup()
                this.$functions.update(this.$route.params.id, this.model).finally(() => {
                    popup.hide()
                })
            }
        },
        async runTest() {
            const popup = this.$loadingPopup()
            const { data } = await this.$http.post(`/v1/system/function/run/${this.model.name}`, this.testInput.body, {
                params: this.testInput.query,
                headers: {
                    'X-Debug': true,
                    ...this.testInput.headers
                }
            }).catch(err => {
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }).finally(popup.hide)

            this.testOutput = data
        },
        async remove() {
            let confirm = await this.$bvModal.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            this.$functions.delete(this.$route.params.id).then(() => {
                this.$router.push('/admin/functions')
            }).catch(() => {
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            })
        },
        validateTag(tag) {
            const camelCaseRegex = /^[a-z][a-zA-Z0-9]*$/;
            const snakeCaseRegex = /^[a-z][a-z0-9_]*$/;
            return camelCaseRegex.test(tag) || snakeCaseRegex.test(tag);
        },

        formatTags() {
            this.model.tags = this.model.tags.map(tag => tag.trim().replaceAll(" ", "_"))
        },

        updateEndpointEnv(env) {
            this.publicEndpointEnv = env
            this.$http.get(`/v1/system/function/signature/${this.publicEndpointEnv}/${this.model.uuid}`).then(resp => {
                const { data } = resp
                
                this.publicEndpoint = data.signature
            })
        }
    },
    mounted() {
        this.$functions.get(this.$route.params.id).then(resp => {
            this.model = resp.data
            this.previewVersion = this.model.dev_version || this.$t('choose')
        }).catch(err => {
            this.$debugLog(err)
            this.$bvModal.msgBoxOk('Failed to fetch model.')
        })

        this.$project.apiUsers().then(resp => {
            this.apiUsers = resp.data
        })
    }
}
</script>

<style scoped>
.terminalLog {
    background-color: black;
    color: #ccc;
    font-size: 15px;
    font-family: 'Lucida Sans Unicode';
    max-height: 400px;
    overflow-y: scroll;
    padding: 10px;
}

.terminalLog ul {
    list-style-type: '→';
}

.terminalLog ul>li {
    padding: 2px;
}
</style>